import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { Sparkles } from '@factorialco/f0-react/icons/app'

import { useEffect } from 'modules/core/hooks/useEffect'

import PriceInfo from 'modules/upsell/components/SelfUpsell/CheckoutModal/PriceInfo'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { BUSINESS_VARIANT, ENTERPRISE_VARIANT } from 'modules/upsell/lib/selfUpsell/plans/utils'
import type { VariantsEnum } from 'modules/upsell/lib/selfUpsell/types'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type CrossellingSelectorProps = {
  crossSellingAddon?: Plan | undefined
  showPrice: boolean
}

const CrossellingSelector = ({ crossSellingAddon, showPrice }: CrossellingSelectorProps) => {
  const {
    configPlan,
    setConfigPlan,
    period,
    toggleCrossSellingPlanSelect,
    currency,
    allCrossSellingAddons,
    discount,
    seats,
    currentSeats,
  } = useSelfUpsellContext()

  useEffect(() => {
    if (!crossSellingAddon) return
    if (!crossSellingAddon?.isSelected) toggleCrossSellingPlanSelect(crossSellingAddon)
  }, [])

  if (!crossSellingAddon) return null

  const handleVariantChange = (variant: VariantsEnum) => {
    toggleCrossSellingPlanSelect(crossSellingAddon)

    crossSellingAddon.setActiveVariant(variant)
    handleToggleCrossSellingPlan()
  }

  const handleToggleCrossSellingPlan = () => {
    if (crossSellingAddon.isSelected) {
      const addons = []
      addons.push(crossSellingAddon.pricing.getPlanId(period) || '', ...(configPlan.addon || []))

      setConfigPlan({
        addon: addons,
        variant: configPlan.variant,
      })
    } else {
      const addons = configPlan.addon?.filter(
        (addon) => addon !== crossSellingAddon.pricing.getPlanId(period) || ''
      )

      setConfigPlan({
        addon: [...(addons || [])],
        variant: configPlan.variant,
      })
    }
  }

  const isCrossSellingVariantAvailable = (variant: VariantsEnum) => {
    return allCrossSellingAddons.f25Addons.some((a) => a.variantTypes.includes(variant))
  }

  return (
    <Box gap="s6">
      <Box gap="s8">
        {isCrossSellingVariantAvailable(ENTERPRISE_VARIANT) && (
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: crossSellingAddon.isSelected ? 'viridian1000' : 'grey400',
              },
            }}
            borderRadius={{ all: 'abs016' }}
            background={crossSellingAddon.isSelected ? 'viridian100' : 'grey000White'}
            padding="s16"
            gap="s8"
            justifyContent="spaceBetween"
            onClick={() => handleVariantChange(ENTERPRISE_VARIANT)}
            style={{
              height: '88px',
            }}
          >
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
              <Text.Normal size="200" weight="semibold" color="grey1000">
                {ENTERPRISE_VARIANT}
              </Text.Normal>
              <F0TagRaw text="Best value" icon={Sparkles} />
            </Box>
            {showPrice && (
              <PriceInfo
                variant={crossSellingAddon.getSpecificVariant(ENTERPRISE_VARIANT)}
                period={period}
                currency={currency}
                seats={seats}
                currentSeats={currentSeats}
                discount={discount}
              />
            )}
          </Box>
        )}

        {isCrossSellingVariantAvailable(BUSINESS_VARIANT) && (
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: crossSellingAddon.isSelected ? 'viridian1000' : 'grey400',
              },
            }}
            borderRadius={{ all: 'abs016' }}
            background={crossSellingAddon.isSelected ? 'viridian100' : 'grey000White'}
            padding="s16"
            gap="s8"
            justifyContent="spaceBetween"
            onClick={() => handleVariantChange(BUSINESS_VARIANT)}
            style={{
              height: '88px',
            }}
          >
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
              <Text.Normal size="200" weight="semibold" color="grey1000">
                {crossSellingAddon.title}
              </Text.Normal>
            </Box>
            {showPrice && (
              <PriceInfo
                variant={crossSellingAddon.getSpecificVariant(BUSINESS_VARIANT)}
                period={period}
                currency={currency}
                seats={seats}
                currentSeats={currentSeats}
                discount={{
                  module: 'one',
                  percentage: 100.0,
                  period: '1',
                  periodUnit: 'year',
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default CrossellingSelector
